import { afterNextRender, Component, computed, DestroyRef, effect, inject, input, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { IconService } from './icon.service';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';

export type IconColorVariant = 'neutral' | 'primary' | 'success' | 'warning' | 'error' | 'white' | undefined;
export type IconWeight = 300 | 400;

@Component({
  selector: 'work-ui-icon',
  standalone: true,
  imports: [TooltipDirective],
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
})
export class IconComponent {
  public name = input.required<string>();
  public boldness = input<IconWeight>(300);
  public filled = input<boolean>(false);
  public framed = input<boolean>(false);
  public tooltipDescription = input<string>('');
  public theme = input<IconColorVariant>(undefined);
  public isLabel = input<boolean>(false);
  public stopPropagation = input<boolean>(false);
  public fontSize = model<number>(24);
  public fontColor = model<IconColorVariant>(undefined);
  public paddingSize = model<number>(8);
  protected isMaterialSymbolsFontLoaded = signal(false);

  protected maxWidth = computed(() => {
    let maxWidth = this.fontSize();
    if (this.framed() || this.theme() !== undefined) {
      // +2 for border width
      maxWidth += 2 * this.paddingSize() + 2;
    }
    return maxWidth;
  });

  private iconService = inject(IconService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    afterNextRender(() => {
      this.iconService.isMaterialSymbolsFontLoaded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (isLoaded) => {
          this.isMaterialSymbolsFontLoaded.set(isLoaded);
        },
      });
    });

    // Effect to handle theme-based fontColor setting
    effect(() => {
      if (this.theme() && !this.fontColor()) {
        this.fontColor.set(this.theme());
      }
    });

    // Effect to handle isLabel adjustments
    effect(() => {
      if (this.isLabel()) {
        this.fontSize.set(18);
        this.paddingSize.set(7);
      }
    });
  }

  protected iconClicked($event: PointerEvent) {
    if (this.stopPropagation()) {
      $event.preventDefault();
      $event.stopPropagation();
    }
  }
}
